/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import * as segment from '@ibm-watson/shared/modules/ia/util/trackers/track-when-ready';
import {
  channelTypeToChannelName,
  PHONE_CHANNEL_TYPE
} from '../util/channelTypes';

// This is different...
const ALL_TOPICS = 'all_topics';

export const SEGMENT_OBJECT_TYPE = 'Integration';

export const SEGMENT = {
  OBJECT_TYPES: {
    INTEGRATION: SEGMENT_OBJECT_TYPE
  },
  EVENTS: {
    VIEWED: 'viewed',
    CLOSED: 'X_clicked',
    NAME_CHANGED: 'nameChanged',
    DESCRIPTION_CHANGED: 'descriptionChanged',
    GO_TO_OAUTH: 'connect_clicked',
    CONNECTED: 'haa_connected',
    CLOSE_CONFIRM_ACCOUNT: 'closeConfirmAccount',
    OPEN_SERVICE_DESK_APP: 'openServiceDeskApp',
    DIALOG_IS_READY: 'dialogIsReady',
    DEFAULT_ESCALATION_TEAM_CHANGED: 'changeDefaultEscalationTeam',
    RULE_ADDED: 'ruleAdded',
    SEARCH_RULE: 'searchRule',
    GO_TO_DIALOG: 'goToDialog',
    RULE_ASSIGNEE_CHANGED: 'ruleAssigneeChanged',
    RULE_TOPIC_CHANGED: 'ruleTopicChanged',
    RULE_ACTION_CHANGED: 'ruleActionChanged',
    RULE_DELETED: 'ruleDeleted',
    MONITORED_INBOX_CHANGED: 'monitoredTeamChanged',
    LEARN_MORE: 'learnMore',
    INBOX_MONITORING_CHANGED: 'inboxMonitoringChanged',
    COPY_INFO_TO_CLIPBOARD: 'copyInformationToClipboard',
    GO_TO_CHANNEL_PAGE: 'goToChannelPage',
    ALLOW_TRANSFERS_TO_LIVE_AGENT_ENABLED: 'allowTransfersToLiveAgentEnabled',
    PHONE_CREATE_CLICKED: 'phoneCreateClicked',
    PHONE_NEW_VIEWED: 'phoneNewViewed',
    PHONE_TEST_CONNECTION_CLICKED: 'phoneTestConnectionClicked',
    WEB_CHAT_SECURITY_ENABLED: 'allowWebChatSecurityFeatures',
    WEB_CHAT_SECURITY_DISABLED: 'removeWebChatSecurityFeatures',
    WEB_CHAT_SECURITY_ENCRYPTION_ENABLED:
      'allowWebChatSecurityEncryptionFeatures',
    WEB_CHAT_HOME_SCREEN_ENABLED: 'allowWebChatHomeScreen',
    WEB_CHAT_HOME_SCREEN_DISABLED: 'removeWebChatHomeScreen',
    WEB_CHAT_BRANDING_ENABLED: 'allowWebChatBranding',
    WEB_CHAT_BRANDING_DISABLED: 'removeWebChatBranding',
    WEB_CHAT_SUGGESTIONS_ENABLED: 'allowWebChatSuggestions',
    WEB_CHAT_SUGGESTIONS_DISABLED: 'removeWebChatSuggestions',
    SET_UP_SERVICE_DESK_BUTTON_CLICKED: 'setUpServiceDeskButtonClicked',
    ACCESS_ACCOUNT_BUTTON_CLICKED: 'accessAccountButtonClicked'
  },
  CLOSE_CONFIRM_ACCOUNT_REASON: {
    RECONNECT: 'reconnect',
    IT_IS_OK: 'it is the right account',
    X_CLICKED: 'X clicked'
  },
  RULE_TYPES: {
    ROUTING: 'transferRules',
    ACTION: 'inboxRules'
  },
  LEARN_MORE_ABOUT: {
    INTEGRATION_SETUP: 'integration_setup',
    PREPARING_THE_DIALOG: 'preparingTheDialog',
    ROUTING: 'routing',
    INBOX_MONITORING: 'inboxMonitoring',
    CONNECTING: 'connecting',
    AGENT_APP: 'agent_app',
    AUTO_ASSIGN: 'auto_assign',
    MANUAL_ASSIGN: 'manual_assign'
  }
};

export function trackIntegrationEvent(integration, eventName, values) {
  const object = channelTypeToChannelName[integration.type];
  // amend the values w/ type and id and segmentData if in integration
  const { segmentCustomData } = integration;
  const amendedValues = {
    ...values,
    ...segmentCustomData,
    waIntegrationType: object,
    waIntegrationId: integration.id
  };
  segment.customEventWithValues(
    eventName,
    object,
    SEGMENT.OBJECT_TYPES.INTEGRATION,
    true,
    null,
    amendedValues
  );
  return true;
}
export function trackIntegrationFailureEvent(integration, eventName, values) {
  const object = channelTypeToChannelName[integration.type];
  segment.customEventWithValues(
    eventName,
    object,
    SEGMENT.OBJECT_TYPES.INTEGRATION,
    false,
    null,
    values
  );
  return true;
}

export function countOfTransferRules(integration) {
  return Object.entries(
    _.get(integration, 'config.routing.handler_by_topic', {})
  ).filter(([topic, { assignee_id }]) => assignee_id && topic !== ALL_TOPICS)
    .length;
}

export function countOfInboxRules(integration) {
  return Object.entries(
    _.get(integration, 'config.routing.handler_by_topic', {})
  ).filter(([topic, { action }]) => action && topic !== ALL_TOPICS).length;
}

export function trackSpeech(integration) {
  const info = PHONE_CHANNEL_TYPE;
  const segData = { info };
  const sttBand = _.get(
    integration,
    'config.private.speech_to_text.band_preference'
  );
  const modelPath =
    sttBand === 'narrowband'
      ? 'config.private.speech_to_text.config.model'
      : 'config.private.speech_to_text.broadband_config.model';
  segData.sttModel = _.get(integration, modelPath);

  const sttLangCustPath =
    sttBand === 'narrowband'
      ? 'config.private.speech_to_text.config.language_customization_id'
      : 'config.private.speech_to_text.broadband_config.language_customization_id';
  const sttLangCustId = _.get(integration, sttLangCustPath);
  segData.sttLanguageCustomized = sttLangCustId ? true : false;

  const sttAcousCustPath =
    sttBand === 'narrowband'
      ? 'config.private.speech_to_text.config.acoustic_customization_id'
      : 'config.private.speech_to_text.broadband_config.acoustic_customization_id';
  const sttAcousCustId = _.get(integration, sttAcousCustPath);
  segData.sttAcousticCustomized = sttAcousCustId ? true : false;
  segData.ttsVoice = _.get(
    integration,
    'config.private.text_to_speech.config.voice'
  );
  const ttsCustId = _.get(
    integration,
    'config.private.text_to_speech.config.customization_id'
  );
  segData.ttsCustomized = ttsCustId ? true : false;

  segData.sipAuthentication = _.get(
    integration,
    'config.private.session_initiation_protocol.security.authentication'
  );
  segData.sipSecureTrunking = _.get(
    integration,
    'config.private.session_initiation_protocol.security.secure_trunking'
  );
  segData.provisionalResponse = _.get(
    integration,
    'config.private.session_initiation_protocol.send_provisional_response'
  );
  segData.holdOnTransfer = _.get(
    integration,
    'config.private.session_initiation_protocol.put_caller_on_hold_on_transfer'
  );
  segData.disconnectTransferFailure = _.get(
    integration,
    'config.private.error_handling.transfer_failure.disconnect_call'
  );
  integration.segmentCustomData = segData;
}
